import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './msalConfig';
import './styles/global.css';

import { UserProvider, UserContext } from './UserContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Core Pages
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Research from './pages/Research';
import Projects from './pages/Projects';
import Signup from './pages/Signup';
import Login from './pages/LoginSignup';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import PaymentCallback from './pages/PaymentCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';

// Dashboards
import VentingRoomDashboard from './pages/VentingRoomDashboard';
import EntrepreneurshipEducationDashboard from './pages/EntrepreneurshipEducationDashboard';
import STEMSubjectsDashboard from './pages/STEMSubjectsDashboard';

// Project Modules
import TalentBasedCurriculum from './components/projects/TalentBasedCurriculum';
import GiantTeacher from './components/projects/GiantTeacher';
import VentingRoom from './components/projects/VentingRoom';
import Freelance from './components/projects/Freelance';
import Entrepreneurship from './components/projects/Entreprenuership';
import STEMSubjects from './components/projects/STEMSubjects';
import BundledDashboard from './pages/BundledDashboard';

// Module Content
import ModuleContentPage from './pages/EntrepreneurshipEducationContent/ModuleContentPage';

const App = () => {
  return (
    <UserProvider>
      <Router>
        <MsalProvider instance={msalInstance}>
          <div className="app-container">
            <Navbar />
            <main className="main-content">
              <AppRoutes />
            </main>
            <Footer />
          </div>
        </MsalProvider>
      </Router>
    </UserProvider>
  );
};

// This component handles all the routing logic based on user authentication and plan status
const AppRoutes = () => {
  const { isAuthenticated, userStatus, isStatusLoading } = useContext(UserContext);

  // Show a loading screen while we determine the user's authentication and plan status.
  if (isStatusLoading) {
    return <div>Loading...</div>; // You can replace this with a better loading component
  }

  // --- Dynamic Routing Logic ---
  const redirectOnLogin = () => {
    // Check if userStatus exists before accessing its properties
    if (userStatus && userStatus.plan === 'venting' && userStatus.voucherStatus === 'valid') {
      return <Navigate to="/venting-room-dashboard" />;
    }
    // For any other case (no plan, expired voucher), redirect to the checkout page.
    return <Navigate to="/checkout" />;
  };

  // Dashboard Protection Wrapper
  const ProtectedRoute = ({ children, requiredPlan }) => {
    // If not authenticated, always redirect to the login page
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    // Check if userStatus exists before accessing its properties
    if (userStatus && (userStatus.plan !== requiredPlan || userStatus.voucherStatus !== 'valid')) {
      return <Navigate to="/checkout" />;
    }
    return children;
  };

  return (
    <Routes>
      {/* Landing & Auth */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/payment-callback" element={<PaymentCallback />} />

      {/* Legal */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />

      {/* Dashboard Redirector */}
      <Route path="/dashboard" element={redirectOnLogin()} />

      {/* Individual Dashboards - now protected */}
      <Route 
        path="/venting-room-dashboard/*" 
        element={
          <ProtectedRoute requiredPlan="venting">
            <VentingRoomDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/entrepreneurship-education-dashboard/*" 
        element={
          <ProtectedRoute requiredPlan="entrepreneurship">
            <EntrepreneurshipEducationDashboard />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/stem-subjects-dashboard/*" 
        element={
          <ProtectedRoute requiredPlan="stem">
            <STEMSubjectsDashboard />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/bundled-dashboard" 
        element={
          <ProtectedRoute requiredPlan="bundled">
            <BundledDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Project Pages */}
      <Route path="/research" element={<Research />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/talent-based-curriculum" element={<TalentBasedCurriculum />} />
      <Route path="/giant-teacher" element={<GiantTeacher />} />
      <Route path="/venting-room" element={<VentingRoom />} />
      <Route path="/freelance" element={<Freelance />} />
      <Route path="/entrepreneurship" element={<Entrepreneurship />} />
      <Route path="/stem-subjects" element={<STEMSubjects />} />

      {/* Module Content */}
      <Route path="/entrepreneurship-education-dashboard/module-content" element={<ModuleContentPage />} />
    </Routes>
  );
};

export default App;
